// User Layout - With Permission Logic and Overview/Support
import React, { Suspense, useEffect, useState } from "react";
import { Portal, Box, useDisclosure, Flex, Icon } from "@chakra-ui/react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Import all route collections
import newRoutes from "common/routes.js";
import { vaultRoutes, homeRoutes, commonRoutes, recruitmentRoutes, departmentRoutes } from "common/routes";
import { ROLE_PATH } from "../../common/roles";

// Components
import Footer from "common/components/footer/FooterAdmin.js";
import Navbar from "common/components/navbar/NavbarAdmin.js";
import Sidebar from "common/components/sidebar/Sidebar.js";
import { SidebarContext } from "common/contexts/SidebarContext";
import FilterSidebar from "common/components/filterSidebar/FilterSidebar";
import DynamicPage from "common/components/dynamicPage";
import DynamicPageview from "common/components/dynamicPage/DynamicPageview";
import CreateDynamic from "common/components/dynamicPage/create";

// Redux
import { fetchImage } from "../../common/redux/reducers/imageSlice";
import { fetchRouteData } from "../../common/redux/reducers/routeSlice";

// Icons
import { LuChevronRightCircle } from "react-icons/lu";

export default function UserDashboard(props) {
  const { ...rest } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const route = useSelector((state) => state?.route?.data);

  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [openSidebar, setOpenSidebar] = useState(false);
  const [openSubBar, setOpenSubBar] = useState(true);
  const [organizedRoutes, setOrganizedRoutes] = useState([]);
  const [routes, setRoutes] = useState(homeRoutes);

  const user = JSON.parse(localStorage.getItem("user")) || {};

  // Initial subRoute state
  const [subRoute, setSubRoute] = useState(
    location.pathname === "/home"
      ? []
      : location.pathname.split("/").includes("crm")
        ? newRoutes
        : location.pathname.split("/").includes("password")
          ? vaultRoutes
          : location.pathname.split("/").includes("recruit")
            ? recruitmentRoutes
            : commonRoutes
  );

  // ============ PERMISSION LOGIC START ============

  // Enhanced Permission System
  const buildUserPermissions = () => {
    if (user?.role === "superAdmin") {
      return "all"; // SuperAdmin has access to everything
    }

    if (!user?.roles || !Array.isArray(user.roles)) {
      return {};
    }

    // If multiple roles, combine permissions (take the highest permission for each module)
    const combinedAccess = {};

    user.roles.forEach(role => {
      if (role.access && Array.isArray(role.access)) {
        role.access.forEach(item => {
          const existing = combinedAccess[item.title] || {
            create: false,
            update: false,
            delete: false,
            view: false
          };

          // Take the highest permission (true overrides false)
          combinedAccess[item.title] = {
            title: item.title,
            create: existing.create || item.create,
            update: existing.update || item.update,
            delete: existing.delete || item.delete,
            view: existing.view || item.view,
          };
        });
      }
    });

    return combinedAccess;
  };

  // Define static routes that should always be visible regardless of permissions
  const STATIC_ROUTES = new Set([
    "home",
    "overview",
    "support",
    "calendar",
    "reporting & analytics",
    "workflow rules", // Only for superAdmin
    "vault",
    "my passwords",
    "shared with me",
    "shared by me"
  ]);

  // Function to check if a route is static
  const isStaticRoute = (routeName) => {
    if (!routeName) return false;
    return STATIC_ROUTES.has(routeName.toLowerCase());
  };

  // Permission check helper
  const hasPermission = (routeName, permissionType = 'view') => {
    const permissions = buildUserPermissions();

    if (permissions === "all") return true; // SuperAdmin

    // Check if it's a static route (always visible)
    if (isStaticRoute(routeName)) return true;

    const moduleAccess = permissions[routeName];
    if (!moduleAccess) return false; // No permission defined for dynamic routes

    return moduleAccess[permissionType] === true;
  };

  // ============ PERMISSION LOGIC END ============

  // Dynamic route path generator
  const pathName = (name) => {
    return `/crm/${name.replace(/ /g, "-")}`;
  };

  // Dynamic Route Handler - Enhanced version with permissions
  const dynamicRoute = (initialSubRoute) => {
    let apiData = [];
    const updatedSubRoute = subRoute.length > 0 ? subRoute : initialSubRoute;

    route &&
      route?.length > 0 &&
      route?.map((item, i) => {
        let rec = updatedSubRoute.find(
          (route) => route?.name === item?.moduleName
        );

        if (!updatedSubRoute.some((route) => route?.name === item?.moduleName)) {
          const newRoute = [
            {
              name: item?.moduleName.replace(/-/g, " "),
              layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
              path: pathName(item.moduleName),
              iconSrc: item?.icon, // Store as string instead of JSX
              component: DynamicPage,
            },
            {
              name: item?.moduleName.replace(/-/g, " "),
              layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
              under: item?.moduleName,
              parentName: item?.moduleName.replace(/-/g, " "),
              path: `${pathName(item.moduleName)}/:id`,
              iconSrc: item?.icon,
              component: DynamicPageview,
            },
            {
              name: item?.moduleName.replace(/-/g, " "),
              layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
              under: item?.moduleName,
              parentName: item?.moduleName.replace(/-/g, " "),
              path: `${pathName(item.moduleName)}/create`,
              component: CreateDynamic,
            },
          ];
          setSubRoute((pre) => [...pre, ...newRoute]);
        } else if (
          updatedSubRoute.some((route) => route?.name === item?.moduleName) &&
          rec.iconSrc !== item?.icon
        ) {
          const updatedData = updatedSubRoute?.map((i) => {
            if (i.name === item?.moduleName) {
              return {
                ...i,
                iconSrc: item?.icon,
              };
            }
            return i;
          });
          setSubRoute(updatedData);
        }

        if (updatedSubRoute.find((route) => route?.name !== item?.moduleName)) {
          if (!newRoutes.find((route) => route?.name?.toLowerCase() === item?.moduleName?.toLowerCase())) {
            const newRoute = [
              {
                name: item?.moduleName.replace(/-/g, " "),
                layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
                path: pathName(item.moduleName),
                iconSrc: item?.icon,
                component: DynamicPage,
              },
              {
                name: item?.moduleName.replace(/-/g, " "),
                layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
                under: item?.moduleName,
                parentName: item?.moduleName.replace(/-/g, " "),
                path: `${pathName(item.moduleName)}/:id`,
                iconSrc: item?.icon,
                component: DynamicPageview,
              },
              {
                name: item?.moduleName.replace(/-/g, " "),
                layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
                under: item?.moduleName,
                parentName: item?.moduleName.replace(/-/g, " "),
                path: `${pathName(item.moduleName)}/create`,
                component: CreateDynamic,
              },
            ];
            apiData.push(...newRoute);
          }
        }
      });

    let filterData = [...newRoutes, ...apiData];
    const accountsItems = filterData.filter((item) => item.name === "Accounts");
    const dealsItems = filterData.filter((item) => item.name === "Deals");
    const remainingItems = filterData.filter(
      (item) => item.name !== "Accounts" && item.name !== "Deals"
    );

    const propertiesIndex = remainingItems.findIndex(
      (item) => item.name?.trim() === "Products"
    );

    let reorderedData = [...remainingItems];
    if (propertiesIndex !== -1) {
      reorderedData.splice(propertiesIndex, 0, ...accountsItems, ...dealsItems);
    } else {
      reorderedData.push(...accountsItems, ...dealsItems);
    }

    setSubRoute(reorderedData);

    // Store only clean, serializable data
    const serializableData = reorderedData.map(route => ({
      name: route.name,
      path: route.path,
      layout: route.layout,
      under: route.under,
      parentName: route.parentName,
      iconSrc: route.iconSrc
    }));

    try {
      localStorage.setItem("crmSubRoute", JSON.stringify(serializableData));
    } catch (error) {
      console.warn("Could not save routes to localStorage:", error);
    }
  };

  // Route Organization with Permission Checks
  const organizeAllRoutes = () => {
    if (location.pathname === "/home") {
      return [];
    }

    // Get all route collections including dynamic routes
    const storedSubRoute = localStorage.getItem("crmSubRoute");
    let crmRoutes = [];

    try {
      if (storedSubRoute) {
        // Parse stored routes and merge with original routes to get components
        const storedRoutes = JSON.parse(storedSubRoute);
        crmRoutes = storedRoutes.map(storedRoute => {
          // Find the original route with the same path to get the component
          const originalRoute = [...newRoutes, ...subRoute].find(orig =>
            orig && orig.path === storedRoute.path
          );

          return {
            ...storedRoute,
            component: originalRoute?.component || null
          };
        });
      } else {
        crmRoutes = [...newRoutes];
      }
    } catch (error) {
      crmRoutes = [...newRoutes];
    }

    // Helper to extract component properly
    const extractComponent = (comp) => {
      if (!comp) return null;
      if (typeof comp === 'function') return comp;
      if (comp.default && typeof comp.default === 'function') return comp.default;
      if (comp.component && typeof comp.component === 'function') return comp.component;
      if (comp.$typeof || comp._payload) return comp; // React.lazy
      return null;
    };

    // Clean routes to ensure proper component extraction
    crmRoutes = crmRoutes.map(route => {
      if (!route) return null;
      return {
        ...route,
        component: extractComponent(route.component)
      };
    }).filter(route => route && route.component);

    // Get route helper with permission check
    const getRoute = (routeName, routeCollection) => {
      const route = routeCollection.find(route =>
        route && route.name === routeName && route.path && !route.under && route.component
      );

      // Apply permission check for non-admin users
      if (user?.role !== "superAdmin" && route && !hasPermission(routeName)) {
        return null;
      }

      return route;
    };

    // Get subroutes helper with permission check
    const getSubRoutes = (parentName, routeCollection) => {
      return routeCollection.filter(route => {
        if (!route || !route.component) return false;

        let shouldInclude = false;

        if (parentName === "My Passwords") {
          shouldInclude = route.under === "password/my-password";
        } else if (parentName === "Shared With Me") {
          shouldInclude = route.under === "/password/share-with-me";
        } else if (parentName === "Shared By Me") {
          shouldInclude = route.under === "/password/share-by-me";
        } else {
          shouldInclude = (route.parentName === parentName && route.under);
        }

        // Apply permission check for non-admin users
        if (user?.role !== "superAdmin" && shouldInclude && !hasPermission(route.name)) {
          return false;
        }

        return shouldInclude;
      });
    };

    const organized = [];

    // ==================== GET STARTED SECTION ====================

    // 1. Get Started (Overview) - Single route at the top
    const overviewRoute = getRoute("Overview", crmRoutes);
    if (overviewRoute) {
      organized.push({
        name: "Get Started",
        path: overviewRoute.path,
        layout: overviewRoute.layout,
        component: overviewRoute.component,
        icon: null
      });
    }

    // ==================== CRM SECTION ====================

    // 2. CRM - Main section with Dashboard as first item
    const crmSubRoutes = [];

    const homeRoute = getRoute("Home", crmRoutes);
    if (homeRoute) {
      crmSubRoutes.push({
        ...homeRoute,
        name: "Dashboard", 
        icon: null
      });
    }

    // 2.2 Add Leads, Contacts, Accounts, Deals under CRM (with permission check)
    const crmMainRoutes = ["Leads", "Contacts", "Accounts", "Deals"];
    crmMainRoutes.forEach(routeName => {
      const route = getRoute(routeName, crmRoutes);
      if (route) {
        crmSubRoutes.push({
          ...route,
          icon: null
        });
      }
    });

    // 2.3 Tasks - Move under CRM (with permission check)
    const taskRoute = getRoute("Tasks", crmRoutes);
    if (taskRoute) {
      crmSubRoutes.push({
        ...taskRoute,
        name: "Tasks",
        icon: null
      });
    }

    // 2.4 Add other dynamic routes to CRM section (with permission filtering)
    const dynamicRoutes = crmRoutes.filter(route => {
      if (!route || !route.path || route.under || !route.component) return false;

      const isExcluded = [
        "Home", "Overview", "Support", "Leads", "Contacts", "Accounts", "Deals", "Tasks",
        "Meetings", "Calls", "Emails", "Calendar", "Reporting & Analytics", "Workflow Rules", "Products"
      ].includes(route.name);

      if (isExcluded) return false;

      // Exclude authentication and product routes for CRM section
      const isAuthOrProduct = route.name.toLowerCase().includes('signin') ||
        route.name.toLowerCase().includes('sign in') ||
        route.name.toLowerCase().includes('login') ||
        route.name.toLowerCase().includes('forgot') ||
        route.name.toLowerCase().includes('reset') ||
        route.name.toLowerCase().includes('password') ||
        route.name.toLowerCase().includes('auth') ||
        route.name.toLowerCase().includes('product') ||
        route.name.toLowerCase().includes('inventory') ||
        route.name.toLowerCase().includes('catalog');

      if (isAuthOrProduct) return false;

      // Apply permission check for non-admin users
      if (user?.role !== "superAdmin" && !hasPermission(route.name)) {
        return false;
      }

      return true;
    });

    dynamicRoutes.forEach(route => {
      crmSubRoutes.push({
        ...route,
        icon: null
      });
    });

    // 2.5 Products - Move to end of CRM section (with permission check)
    const productsRoute = getRoute("Products", crmRoutes);
    if (productsRoute) {
      crmSubRoutes.push({
        ...productsRoute,
        name: "Products",
        icon: null
      });
    } else {
      const productDynamicRoutes = crmRoutes.filter(route => {
        if (!route || !route.name || !route.component || !route.path || route.under) return false;

        const isProductRoute = route.name.toLowerCase().includes('product') ||
          route.name.toLowerCase().includes('inventory') ||
          route.name.toLowerCase().includes('catalog');

        if (!isProductRoute) return false;

        // Apply permission check for non-admin users
        if (user?.role !== "superAdmin" && !hasPermission(route.name)) {
          return false;
        }

        return true;
      });

      productDynamicRoutes.forEach(route => {
        crmSubRoutes.push({
          name: route.name,
          path: route.path,
          layout: route.layout || [ROLE_PATH.superAdmin, ROLE_PATH.user],
          component: route.component,
          icon: null
        });
      });
    }

    if (crmSubRoutes.length > 0) {
      organized.push({
        name: "CRM", 
        subRoutes: crmSubRoutes
      });
    }

    // 3. Activities - Show based on permissions
    const activityRoutes = [];
    ["Meetings", "Calls", "Emails"].forEach(routeName => {
      const route = getRoute(routeName, crmRoutes);
      if (route) {
        activityRoutes.push({
          ...route,
          icon: null
        });
      }
    });

    // Calendar is always visible (static route)
    const calendarRoute = getRoute("Calendar", crmRoutes);
    if (calendarRoute || isStaticRoute("Calendar")) {
      const route = calendarRoute || crmRoutes.find(r => r && r.name === "Calendar" && r.component);
      if (route) {
        activityRoutes.push({
          ...route,
          icon: null
        });
      }
    }

    if (activityRoutes.length > 0) {
      organized.push({
        name: "Activities",
        subRoutes: activityRoutes
      });
    }

    // 4. Workflows (renamed from Workflow Rules) - SuperAdmin only
    if (user?.role === "superAdmin") {
      const workflowRoute = getRoute("Workflow Rules", crmRoutes);
      if (workflowRoute) {
        organized.push({
          name: "Workflows", 
          path: workflowRoute.path,
          layout: workflowRoute.layout,
          component: workflowRoute.component,
        });
      }
    }

    // 5. Reports (renamed from Reporting) - Always show (static route)
    const reportingRoute = getRoute("Reporting & Analytics", crmRoutes);
    if (reportingRoute || isStaticRoute("Reporting & Analytics")) {
      const route = reportingRoute || crmRoutes.find(r => r && r.name === "Reporting & Analytics" && r.component);
      if (route) {
        organized.push({
          name: "Reports", 
          path: route.path,
          layout: route.layout,
          component: route.component,
        });
      }
    }

    // ==================== SEPARATOR BEFORE PASSWORD ====================
    organized.push({
      name: "separator-password",
      isSeparator: true,
      separatorText: "" 
    });

    // ==================== PASSWORD SECTION ====================

    // 6. Password Manager (renamed from Password) - Always show (static routes)
    const cleanVaultRoutes = vaultRoutes.map(route => {
      if (!route) return null;
      return {
        ...route,
        component: extractComponent(route.component)
      };
    }).filter(route => route && route.component);

    const passwordMainRoutes = [];
    ["My Passwords", "Shared With Me", "Shared By Me"].forEach(routeName => {
      const route = cleanVaultRoutes.find(route =>
        route && route.name === routeName && route.path && !route.under && route.component
      );
      if (route) {
        const subroutes = getSubRoutes(routeName, cleanVaultRoutes);
        passwordMainRoutes.push({
          ...route,
          icon: null,
          subRoutes: subroutes.length > 0 ? subroutes.map(sr => ({ ...sr, icon: null })) : undefined
        });
      }
    });

    if (passwordMainRoutes.length > 0) {
      organized.push({
        name: "Password Manager", 
        subRoutes: passwordMainRoutes
      });
    }

   
    return organized;
  };

  useEffect(() => {
    const storedSubRoute = localStorage.getItem("crmSubRoute");
    if (location.pathname === "/home") {
      setSubRoute([]);
    } else if (location.pathname.split("/").includes("crm")) {
      const initialSubRoute = storedSubRoute ? JSON.parse(storedSubRoute) : newRoutes;
      if (initialSubRoute.length > 0) {
        setSubRoute(prevSubRoute => {
          const existingPaths = new Set(prevSubRoute.map(r => r.path));
          const newRoutes = initialSubRoute.filter(r => !existingPaths.has(r.path));
          return [...prevSubRoute, ...newRoutes];
        });
      }
    } else if (location.pathname.split("/").includes("password")) {
      setSubRoute(prevSubRoute => {
        const existingPaths = new Set(prevSubRoute.map(r => r.path));
        const newVaultRoutes = vaultRoutes.filter(r => !existingPaths.has(r.path));
        return [...prevSubRoute, ...newVaultRoutes];
      });
    } else if (location.pathname.split("/").includes("recruit")) {
      setSubRoute(prevSubRoute => {
        const existingPaths = new Set(prevSubRoute.map(r => r.path));
        const newRecruitRoutes = recruitmentRoutes.filter(r => !existingPaths.has(r.path));
        return [...prevSubRoute, ...newRecruitRoutes];
      });
    } else {
      // For routes like /overview and /support, use the main routes
      const storedSubRoute = localStorage.getItem("crmSubRoute");
      const mainRoutes = storedSubRoute ? JSON.parse(storedSubRoute) : newRoutes;
      setSubRoute(prevSubRoute => {
        const existingPaths = new Set(prevSubRoute.map(r => r.path));
        const newMainRoutes = mainRoutes.filter(r => !existingPaths.has(r.path));
        return [...prevSubRoute, ...newMainRoutes];
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (route?.length > 0) {
      dynamicRoute(subRoute.length > 0 ? subRoute : newRoutes);
    }
  }, [route]);

  useEffect(() => {
    const routes = organizeAllRoutes();
    setOrganizedRoutes(routes);
  }, [location.pathname, route, user?.role, subRoute]);

  // Utility functions
  const getRoute = () => window.location.pathname !== "/admin/full-screen-maps";

  const defaultRoute = [
    "crm/default", "recruit/dashboard", "crm/contactView", "crm/leadView", "/home"
  ].some((path) => location.pathname.includes(path));

  const topRoute = [
    "/recruit/candidate", "/recruit/jobProfileCreate", "recruit/InterviewCreate",
    "recruit/jobApplicationCreate", "recruit/createCandidate", "recruit/departmentCreate",
    "recruit/offerLetterCreate"
  ].some((path) => location.pathname.includes(path));

  // Helper functions for navbar
  const getActiveRoute = (routes) => {
    let activeRoute = "pulseCRM pro";
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else if (routes[i].category) {
        let categoryActiveRoute = getActiveRoute(routes[i].items);
        if (categoryActiveRoute !== activeRoute) {
          return categoryActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].path.replace("/:id", "")) !==
          -1
        ) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };

  const under = (routes) => {
    let activeRoute = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else if (routes[i].category) {
        let categoryActiveRoute = getActiveRoute(routes[i].items);
        if (categoryActiveRoute !== activeRoute) {
          return categoryActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].path.replace("/:id", "")) !==
          -1
        ) {
          return routes[i];
        }
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbar(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (window.location.href.indexOf(routes[i].path) !== -1) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (window.location.href.indexOf(routes[i].path) !== -1) {
          return routes[i].messageNavbar;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes) => {
    const validRoutes = [];

    const extractComponent = (comp) => {
      if (!comp) return null;

      if (typeof comp === 'function') return comp;

      if (comp.default && typeof comp.default === 'function') return comp.default;

      if (comp.component && typeof comp.component === 'function') return comp.component;

      if (comp.$typeof || comp._payload) return comp;

      return null;
    };

    const processRoute = (prop, key) => {
      if (!prop || !prop.path) return null;

      const Component = extractComponent(prop.component);

      if (!Component) {
        return null;
      }

      const userRole = user?.role === "superAdmin" ? ROLE_PATH.superAdmin : ROLE_PATH.user;
      if (prop.layout && Array.isArray(prop.layout) && !prop.layout.includes(userRole)) {
        return null;
      }

      try {
        if (!prop.under && prop.layout?.includes(userRole)) {
          validRoutes.push(
            <Route path={prop.path} element={<Component />} key={`main-${key}`} />
          );
        } else if (prop.under) {
          validRoutes.push(
            <Route path={prop.path} element={<Component />} key={`sub-${key}`} />
          );
        }
      } catch (error) {
        console.error(`Error creating route for ${prop.path}:`, error);
        return null;
      }

      if (prop.collapse) {
        prop.items?.forEach((item, index) => processRoute(item, `${key}-collapse-${index}`));
      }
      if (prop.category) {
        prop.items?.forEach((item, index) => processRoute(item, `${key}-category-${index}`));
      }
      if (prop.subRoutes) {
        prop.subRoutes.forEach((item, index) => processRoute(item, `${key}-sub-${index}`));
      }
    };

    if (Array.isArray(routes)) {
      routes.forEach((route, index) => processRoute(route, index));
    }

    return validRoutes;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchRouteData());
        await dispatch(fetchImage());
      } catch (error) {
        console.error("Error dispatching actions:", error);
      }
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (openSubBar) {
      document.body.classList.add('openSidebar');
    } else {
      document.body.classList.remove('openSidebar');
    }
    return () => document.body.classList.remove('openSidebar');
  }, [openSubBar]);

  const largeLogo = useSelector((state) =>
    state?.images?.image?.filter((item) => item.isActive === true)
  );

  const { onOpen } = useDisclosure();
  document.documentElement.dir = "ltr";

  return (
    <Box>
      <SidebarContext.Provider value={{ toggleSidebar, setToggleSidebar }}>
        <Sidebar
          organizedRoutes={organizedRoutes}
          routes={routes}
          subRoute={subRoute}
          isUserLayout={true}
          largeLogo={largeLogo}
          display="none"
          openSubBar={openSubBar}
          setOpenSubBar={setOpenSubBar}
          openSidebar={openSidebar}
          setOpenSidebar={setOpenSidebar}
          {...rest}
        />
        <FilterSidebar />

        <Box
          className="dash-cont"
          float="right"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          maxHeight="100%"
          w={{
            base: "100%",
            xl: openSidebar === true
              ? "calc( 100% - 300px )"
              : openSubBar === true
                ? "calc( 100% - 500px )"
                : !openSidebar && !openSubBar
                  ? "calc( 100% - 88px )"
                  : "calc( 100% - 200px )",
          }}
          maxWidth={{
            base: "100%",
            xl: openSidebar === true
              ? "calc( 100% - 300px )"
              : openSubBar === true
                ? "calc( 100% - 500px )"
                : !openSidebar && !openSubBar
                  ? "calc(100% - 150px)"
                  : "calc( 100% - 200px )",
          }}
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
          transitionDuration=".2s, .2s, .35s"
          transitionProperty="top, bottom, width"
          transitionTimingFunction="linear, linear, ease"
        >
          <Portal>
            <Box className="header">
              <Navbar
                isUserLayout={user?.role !== "superAdmin"}
                onOpen={onOpen}
                logoText={"Horizon UI Dashboard PRO"}
                brandText={getActiveRoute(subRoute)}
                secondary={getActiveNavbar(subRoute)}
                message={getActiveNavbarText(subRoute)}
                routes={routes}
                organizedRoutes={organizedRoutes}
                subRoute={subRoute}
                fixed={fixed}
                under={under(subRoute)}
                largeLogo={largeLogo}
                openSubBar={openSubBar}
                setOpenSubBar={setOpenSubBar}
                openSidebar={openSidebar}
                setOpenSidebar={setOpenSidebar}
                {...rest}
              />
            </Box>
          </Portal>

          <Box
            pt={{ base: "150px", md: "95px", xl: "95px" }}
            className={`main-content ${defaultRoute ? "defaultPage" : ""} ${topRoute ? "ptNormal" : ""}`}
          >
            {getRoute() && (
              <Box
                mx="auto"
                pe="20px"
                w="50%"
                minH="84vh"
                pt="50px"
                style={{ padding: openSidebar ? "8px 20px 8px 20px" : "8px 20px" }}
              >
                <Suspense
                  fallback={
                    <Flex justifyContent="center" alignItems="center" width="100%">
                      {/* <Spinner /> */}
                    </Flex>
                  }
                >
                  <Routes>
                    {getRoutes(
                      location.pathname === "/home"
                        ? homeRoutes
                        : subRoute
                    )}
                    <Route path="/superAdmin" element={<Navigate to="/crm/default" />} />
                  </Routes>
                </Suspense>
              </Box>
            )}
          </Box>

          <Box>
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}